'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    AnimatePresence,
    MotionConfig,
    motion,
    useIsPresent,
} from 'framer-motion';

import s from './BottomSheet.module.scss';

const SLIDE = { duration: 0.25, ease: 'easeOut' };
const FOLLOW = { ...SLIDE, height: { duration: 0 } };

const SheetContext = createContext(null);

// The open sheet around what is in it, so it can tell what the sheet covers.
// Null outside a sheet.
export const useBottomSheet = () => useContext(SheetContext);

// Once it starts to close, it is gone for readers and assistive technology,
// while it slides away.
const Sliding = ({ ref, className, height, isFollowing, children }) => {
    const isPresent = useIsPresent();
    return (
        <motion.div
            ref={ref}
            className={className}
            initial={{ y: '100%' }}
            animate={{ y: '0%', height }}
            exit={{ y: '100%' }}
            transition={isFollowing ? FOLLOW : SLIDE}
            inert={!isPresent || undefined}
            aria-hidden={!isPresent || undefined}>
            {children}
        </motion.div>
    );
};

// Slides up from the bottom of the screen to open, and down to close. Readers
// who ask for less motion get it without the slide.
export const SlideUp = ({
    isOpen,
    ref,
    className,
    height,
    isFollowing,
    children,
}) => (
    <MotionConfig reducedMotion="user">
        <AnimatePresence>
            {isOpen && (
                <Sliding
                    ref={ref}
                    className={className}
                    height={height}
                    isFollowing={isFollowing}>
                    {children}
                </Sliding>
            )}
        </AnimatePresence>
    </MotionConfig>
);

// Fixed to the bottom of the screen, for what has no room beside the report
// on phones and tablets. What is in it scrolls inside it. The page behind
// stays as it is: there is no backdrop and no scroll lock, and closing is up
// to what is in it.
//
// It grows and shrinks with what is in it as it slides, up to its max-height.
// Its height is set from what it holds, since CSS can't animate to auto.
const BottomSheet = ({ isOpen, children }) => {
    const [sheet, setSheet] = useState(null);
    const [content, setContent] = useState(null);
    const [{ height, isFollowing }, setSize] = useState({});
    useEffect(() => {
        if (!sheet || !content) return;
        const observer = new ResizeObserver(() => {
            const style = getComputedStyle(sheet);
            const frame = ['borderTopWidth', 'paddingTop', 'paddingBottom']
                .map((side) => parseFloat(style[side]))
                .reduce((sum, size) => sum + size);
            // Capped, so shrinking from full height starts at once.
            const next = Math.min(
                content.offsetHeight + frame,
                parseFloat(style.maxHeight)
            );
            const follows = content
                .getAnimations({ subtree: true })
                .some((animation) =>
                    /height/.test(animation.transitionProperty)
                );
            if (follows) sheet.style.height = `${next}px`;
            setSize({ height: next, isFollowing: follows });
        });
        observer.observe(content);
        return () => observer.disconnect();
    }, [sheet, content]);
    return (
        <SlideUp
            isOpen={isOpen}
            ref={setSheet}
            className={s.Root}
            height={height}
            isFollowing={isFollowing}>
            <SheetContext.Provider value={sheet}>
                <div ref={setContent}>{children}</div>
            </SheetContext.Provider>
        </SlideUp>
    );
};

export default BottomSheet;
